import { List, useAccordionItemContext } from '@chakra-ui/react';
import {
  AggregationModelEnum,
  IPriceReqSchema,
  IProduct,
  OfferingTypesEnum,
  PriceModelEnum,
  ProductTypeEnum,
} from '@monetize/types/app';
import React, { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { MdContentCopy, MdContentPaste, MdFileCopy } from 'react-icons/md';
import { v4 as uuidv4 } from 'uuid';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionCustomButtonItem,
  MAccordionItem,
  MAccordionPanel,
  MCustomIconButton,
  MFlex,
  MFormField,
  MLockedTextOrContent,
  MTable,
  MTbody,
  MTh,
  MThead,
  MTooltip,
  MTr,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { OFFERINGS } from '../../../../../constants';
import { PRODUCT_TYPE_DISPLAY_SHORT } from '../../../../../constants/products';
import { useFlags } from '../../../../../services/launchDarkly';
import { getNextTierPrice } from '../../../../../utils/product';
import HeaderCellText from '../HeaderCellText';
import RateMinimumCommitConfig from './RateMinimumCommitConfig';
import RatePriceTableRow from './RatePrice';
import { getInitialQuantityByProductType } from './RatePriceUtils';

type FormContextReturn = ReturnType<typeof useFormContext>;
type PriceFieldArrayReturn = ReturnType<typeof useFieldArray>;

interface RatePricesAccordionBodyProps {
  product: IProduct;
  offeringType: OfferingTypesEnum;
  loading?: boolean;
  isLocked?: boolean;
  currency: string;
  isExistingRate: boolean;
  isReadOnly?: boolean;
  isMultipleProduct: boolean;
  pricesToCopyFromProductId?: string;
  setPricesToCopyFromProductId: (id: string) => void;
  pricesLengthFromApi?: number;
  enableBlockPricing: boolean;
  fields: PriceFieldArrayReturn['fields'];
  append: PriceFieldArrayReturn['append'];
  control: FormContextReturn['control'];
  getValues: FormContextReturn['getValues'];
  dirtyFields: FormContextReturn['formState']['dirtyFields'];
  aggregationModelError: any;
  aggregationModelFieldKey: string;
  isPoTOfferingType: boolean;
  isMinCommitProduct: boolean;
  isUsageTypeProduct: boolean;
  isBlockPricing: boolean;
  handleRemovePrice: (index: number) => void;
  handleCopyPricesFromProductId: () => void;
  handlePastePricesFromProductId: () => void;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const RatePricesAccordionBody = ({
  product,
  offeringType,
  loading,
  isLocked,
  currency,
  isExistingRate,
  isReadOnly,
  isMultipleProduct,
  pricesToCopyFromProductId,
  setPricesToCopyFromProductId,
  pricesLengthFromApi,
  enableBlockPricing,
  fields,
  append,
  control,
  getValues,
  dirtyFields,
  aggregationModelError,
  aggregationModelFieldKey,
  isPoTOfferingType,
  isMinCommitProduct,
  isUsageTypeProduct,
  isBlockPricing,
  handleRemovePrice,
  handleCopyPricesFromProductId,
  handlePastePricesFromProductId,
}: RatePricesAccordionBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <MFlex flexDir="column" py={2}>
      <MAccordionCustomButton // product price accordion
        buttonProps={{
          paddingLeft: 0,
          paddingBottom: '0',
          _hover: {
            bg: 'white',
          },
        }}
        labelProps={{
          fontSize: 'sm',
          paddingBottom: '0',
        }}
        isExpanded={isExpanded}
        label={`${product.name} (${PRODUCT_TYPE_DISPLAY_SHORT[product.productType]})`}
        data-testid="toggle-product-icon"
      >
        <MFlex
          alignItems="center"
          maxW={isExpanded ? '44.125rem' : '100%'}
          justify="space-between"
          pt={0}
          pb={0}
          onClick={(e) => e.stopPropagation()}
        >
          {!isExpanded && (
            <MAccordionCustomButtonItem
              labelTextProps={{ fontSize: 'sm' }}
              label="Tiers"
              valueTextProps={{ color: 'tPurple.safety' }}
              value={fields.length}
            />
          )}

          <MFlex alignItems="center">
            {isExpanded && isMinCommitProduct && (
              <RateMinimumCommitConfig
                product={product}
                isLoading={loading}
                isLocked={isLocked || isReadOnly}
                isExistingRate={isExistingRate}
              />
            )}

            {isExpanded && isUsageTypeProduct && (
              <MFormField
                isHorizontal
                isRequired={!isLocked}
                label="Aggregation Model"
                error={aggregationModelError}
                justifyContent="flex-end"
                w="initial"
                mr="4"
              >
                <MLockedTextOrContent
                  isLocked={Boolean(isLocked)}
                  text={
                    OFFERINGS.AGGREGATION_MODEL_OPTIONS[
                      getValues(
                        aggregationModelFieldKey,
                      ) as AggregationModelEnum
                    ]
                  }
                >
                  <Controller
                    name={aggregationModelFieldKey}
                    defaultValue={AggregationModelEnum.SUM}
                    control={control}
                    render={({ field }) => (
                      <MCustomSelect
                        w="32"
                        isDisabled={loading}
                        isReadOnly={isReadOnly}
                        bgColor="tWhite.base"
                        placeholder="Select"
                        items={OFFERINGS.AGGREGATION_MODEL_UI_OPTIONS}
                        variant={
                          isReadOnly
                            ? 'readonly'
                            : !!product.id &&
                                dirtyFields?.products?.[product.id]
                                  ?.aggregationModel
                              ? 'unsaved'
                              : 'outline'
                        }
                        {...field}
                      />
                    )}
                  />
                </MLockedTextOrContent>
              </MFormField>
            )}

            {isMultipleProduct &&
              !isLocked &&
              !isMinCommitProduct &&
              isExpanded && (
                <MFlex minW="14" justifyContent="flex-end">
                  <MTooltip
                    label={'Copy Pricing Model, Up to and From'}
                    placement="bottom-start"
                    isDisabled={pricesToCopyFromProductId === product.id}
                  >
                    <MCustomIconButton
                      containerSize={7}
                      btnSize={4}
                      variant="icon"
                      icon={
                        pricesToCopyFromProductId === product.id
                          ? MdFileCopy
                          : MdContentCopy
                      }
                      aria-label="Copy prices"
                      onClick={handleCopyPricesFromProductId}
                    />
                  </MTooltip>
                  {!!pricesToCopyFromProductId &&
                    pricesToCopyFromProductId !== product.id && (
                      <MTooltip
                        label={'Paste Pricing Model, Up to and From'}
                        placement="bottom-start"
                        isDisabled={pricesToCopyFromProductId === product.id}
                      >
                        <MCustomIconButton
                          containerSize={7}
                          btnSize={4}
                          variant="icon"
                          icon={MdContentPaste}
                          aria-label="Paste prices"
                          onClick={handlePastePricesFromProductId}
                        />
                      </MTooltip>
                    )}
                </MFlex>
              )}
          </MFlex>
        </MFlex>
      </MAccordionCustomButton>

      {isExpanded && (
        <MAccordionPanel px={2} pt={0} pr={0} pb={2}>
          {/* v3 has no `container.*` size tokens: `7xl`/`3xl` are v2's
              `container.xl` (1280px) / `container.md` (768px). */}
          <MTable
            maxWidth={
              offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL
                ? '7xl'
                : '3xl'
            }
            size="sm"
            css={{
              // Matches the Table theme's `thead th`/`tbody td` padding: what v2
              // rendered for header and body cells alike, its nested theme
              // selectors outranking this table's own `px: 2, py: 1` override.
              // Pinned so `size="sm"` can't shrink them, and kept equal so each
              // header label lines up with the input below it.
              '& th, & td': {
                py: '8px',
                px: '12px',
              },
            }}
          >
            <MThead>
              <MTr _hover={{ bg: 'unset' }}>
                <MTh w="1%" />
                <MTh>
                  <HeaderCellText
                    isRequired={!isLocked}
                    tooltipLabel={
                      <List.Root as="ul">
                        <List.Item>
                          <strong>Volume:</strong> All units are charged at the
                          price tier corresponding to the total volume.
                        </List.Item>
                        <List.Item>
                          <strong>Tiered:</strong> The total price is calculated
                          cumulatively. For each tier, multiply the number of
                          units in that tier by its price, then add these
                          amounts together.
                        </List.Item>
                        <List.Item>
                          <strong>Flat:</strong> Flat price regardless of the
                          number of units.
                        </List.Item>
                        <List.Item>
                          <strong>Custom:</strong> The price is manually
                          specified during the sales process.
                        </List.Item>
                        {enableBlockPricing && (
                          <List.Item>
                            <strong>Block:</strong> Pricing is based on
                            predefined quantity blocks. You can choose to keep
                            all blocks the same size and price, or define custom
                            sizes and rates for each block.
                          </List.Item>
                        )}
                      </List.Root>
                    }
                  >
                    Pricing Model
                  </HeaderCellText>
                </MTh>
                <MTh
                  fontWeight="bold"
                  textTransform="none"
                  fontSize="xs"
                  {...(isPoTOfferingType && { w: '8rem' })}
                >
                  <HeaderCellText
                    isRequired={isPoTOfferingType ? false : !isLocked}
                  >
                    {isPoTOfferingType ? 'Minimum Total' : 'From'}
                  </HeaderCellText>
                </MTh>
                <MTh
                  fontWeight="bold"
                  textTransform="none"
                  fontSize="xs"
                  {...(isPoTOfferingType && { w: '8rem' })}
                >
                  <HeaderCellText>
                    {isPoTOfferingType ? 'Maximum Total' : 'Up to'}
                  </HeaderCellText>
                </MTh>
                {isBlockPricing && (
                  <MTh fontWeight="bold" textTransform="none" fontSize="xs">
                    <HeaderCellText>Block Size</HeaderCellText>
                  </MTh>
                )}
                <MTh
                  fontWeight="bold"
                  textTransform="none"
                  fontSize="xs"
                  {...(isPoTOfferingType && { w: '8rem' })}
                >
                  <HeaderCellText
                    isRequired={!isLocked}
                    tooltipLabel={
                      isPoTOfferingType &&
                      'Price determined on Quote based on this percentage applied to all or specific Products'
                    }
                  >
                    {isPoTOfferingType ? 'Percentage' : 'Amount'}
                  </HeaderCellText>
                </MTh>
                <MTh w={isPoTOfferingType ? 'auto' : '8'}>
                  {isPoTOfferingType && (
                    <HeaderCellText textProps={{ marginBottom: 0.5 }}>
                      Scoped Products
                    </HeaderCellText>
                  )}
                </MTh>
              </MTr>
            </MThead>
            <MTbody>
              {fields.map(
                (field, i) =>
                  product.id && (
                    <RatePriceTableRow
                      index={i}
                      key={field.pId}
                      id={field.pId}
                      isOpen={isExpanded}
                      product={product}
                      isLastRow={fields.length - 1 === i}
                      lastRowIndex={fields.length - 1}
                      offeringType={offeringType}
                      handleRemovePrice={handleRemovePrice}
                      isBlockPricing={isBlockPricing}
                      generateNewPrice={(
                        previousFrom,
                        previousTo,
                        previousPriceModel,
                        previousAmount,
                        previousBlockSize,
                      ) =>
                        append(
                          getNextTierPrice({
                            productId: product.id!,
                            previousFrom,
                            previousTo,
                            previousPriceModel,
                            previousAmount,
                            previousBlockSize,
                          }),
                        )
                      }
                      isLocked={isLocked}
                      loading={loading}
                      currency={currency}
                      pricesLengthFromApi={pricesLengthFromApi}
                      isReadOnly={isReadOnly}
                    />
                  ),
              )}
            </MTbody>
          </MTable>
        </MAccordionPanel>
      )}
    </MFlex>
  );
};

interface RatePricesProps {
  product: IProduct;
  offeringType: OfferingTypesEnum;
  pricesLengthFromApi?: number;
  loading?: boolean;
  isLocked?: boolean;
  isLastRow?: boolean;
  currency: string;
  isExistingRate?: boolean;
  isReadOnly?: boolean;
  pricesToCopyFromProductId?: string;
  isMultipleProduct?: boolean;
  isProductExpanded: boolean;
  setPricesToCopyFromProductId: (id: string) => void;
}

const RatePrices = React.forwardRef<HTMLInputElement | null, RatePricesProps>(
  (
    {
      loading,
      product,
      isLocked,
      isLastRow,
      currency,
      pricesLengthFromApi,
      isExistingRate = false,
      isReadOnly,
      offeringType,
      pricesToCopyFromProductId,
      isMultipleProduct = false,
      isProductExpanded,
      setPricesToCopyFromProductId,
    }: RatePricesProps,
    ref: React.ForwardedRef<HTMLInputElement | null>,
  ) => {
    const { enableBlockPricing } = useFlags();
    const {
      setValue,
      control,
      formState: { errors, dirtyFields },
      getValues,
      clearErrors,
      watch,
    } = useFormContext();

    const aggregationModelError = (errors?.products as any)?.[product.id!]
      ?.aggregationModel;
    const isUsageTypeProduct = product.productType === ProductTypeEnum.USAGE;
    const isMinCommitProduct =
      product.productType === ProductTypeEnum.MIN_COMMIT;
    const aggregationModelFieldKey = `products.${product.id}.aggregationModel`;
    const isPoTOfferingType =
      offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL;

    const prices = watch(`products.${product.id}.prices`);
    const isBlockPricing =
      enableBlockPricing &&
      prices?.some((pricing) => pricing.priceModel === PriceModelEnum.BLOCK);

    const { fields, append, remove } = useFieldArray({
      control,
      name: `products.${product.id}.prices`,
      keyName: 'pId',
    });

    const handleRemovePrice = (index: number) => {
      // Make sure to clear any errors while removing a price tier
      const priceErrors = (errors?.products as Record<string, any>)?.[
        product.id!
      ]?.prices;

      if (priceErrors) {
        clearErrors(`products.${product.id}.prices`);
      }

      const rowToRemove = getValues(
        `products.${product.id}.prices.${index}`,
      ) as IPriceReqSchema;

      // Remove first, then adjust the shifted row's "from" value
      remove(index);

      if (index !== fields.length - 1) {
        /** adjust next row (now at `index` after removal) from value with current row from value */
        setValue(
          `products.${product.id}.prices.${index}.from`,
          rowToRemove.from,
          { shouldValidate: true, shouldDirty: false },
        );
      }
    };

    const handlePastePricesFromProductId = () => {
      if (pricesToCopyFromProductId) {
        const prices = getValues(
          `products.${pricesToCopyFromProductId}.prices`,
        );
        const currentProductPrices = getValues(`products.${product.id}.prices`);
        if (prices && Array.isArray(prices)) {
          const preparedPricesToPaste = prices.map(
            ({ productId, ...rest }: IPriceReqSchema, index: number) => {
              const amount = currentProductPrices?.[index]?.amount
                ? currentProductPrices[index].amount
                : '';
              if (isUsageTypeProduct && index === 0) {
                return {
                  ...rest,
                  from: rest.from !== 0 ? 0 : rest.from,
                  productId: product.id,
                  amount,
                };
              }

              if (isUsageTypeProduct && index === prices.length - 1) {
                return {
                  ...rest,
                  to: rest.to !== null ? null : rest.to,
                  productId: product.id,
                  amount,
                };
              }

              return {
                ...rest,
                productId: product.id,
                amount,
              };
            },
          );
          setValue(`products.${product.id}.prices`, preparedPricesToPaste);
        }
      }
    };

    const handleCopyPricesFromProductId = () => {
      if (product.id) {
        setPricesToCopyFromProductId(product.id);
      }
    };

    useEffect(() => {
      if (fields.length === 0) {
        if (isPoTOfferingType) {
          const uid = uuidv4();
          append({
            id: uid,
            productId: product.id,
            description: '',
            priceModel: PriceModelEnum.PERCENT_OF_TOTAL,
            from: 1,
            to: 1,
            amount: 1,
          });
        } else {
          append(
            getNextTierPrice({
              productId: product.id ?? '',
              previousFrom: getInitialQuantityByProductType(
                product.productType,
              ),
              previousTo: null,
              previousAmount: null,
            }),
          );
        }
      }
    }, [
      append,
      fields.length,
      isPoTOfferingType,
      product.id,
      product.productType,
    ]);

    return (
      isProductExpanded && (
        <MAccordion
          variant="default"
          collapsible
          defaultValue={fields.length <= 25 ? ['0'] : []}
        >
          <MAccordionItem
            value="0"
            w="100%"
            border="0"
            data-testid="product-under-rate-form"
          >
            <RatePricesAccordionBody
              product={product}
              offeringType={offeringType}
              loading={loading}
              isLocked={isLocked}
              currency={currency}
              isExistingRate={isExistingRate}
              isReadOnly={isReadOnly}
              isMultipleProduct={isMultipleProduct}
              pricesToCopyFromProductId={pricesToCopyFromProductId}
              setPricesToCopyFromProductId={setPricesToCopyFromProductId}
              pricesLengthFromApi={pricesLengthFromApi}
              enableBlockPricing={enableBlockPricing}
              fields={fields}
              append={append}
              control={control}
              getValues={getValues}
              dirtyFields={dirtyFields}
              aggregationModelError={aggregationModelError}
              aggregationModelFieldKey={aggregationModelFieldKey}
              isPoTOfferingType={isPoTOfferingType}
              isMinCommitProduct={isMinCommitProduct}
              isUsageTypeProduct={isUsageTypeProduct}
              isBlockPricing={isBlockPricing}
              handleRemovePrice={handleRemovePrice}
              handleCopyPricesFromProductId={handleCopyPricesFromProductId}
              handlePastePricesFromProductId={handlePastePricesFromProductId}
            />
          </MAccordionItem>
        </MAccordion>
      )
    );
  },
);

export default RatePrices;
